import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { isNil } from 'lodash';

/** One labelled list a section's data can contribute to the read-only view. */
interface IValueList {
  /** Translation key for the list heading, or null when the list needs no heading */
  labelKey: string | null;
  items: string[];
}

/**
 * Renders one section's data read only, for the AI comparison view.
 *
 * A deliberately separate, flattened renderer rather than a reuse of the
 * editing section components: those are `ControlValueAccessor` form controls
 * built for editing one live section, and the comparison view needs two
 * inert copies of arbitrary section data side by side.
 *
 * Every section type shares `{ title, subtitle? }` and then differs: rich text
 * `content`, or one of several item lists. This reads whichever are present
 * rather than switching on the type id, so a section type added later still
 * renders something sensible instead of nothing.
 */
@Component({
  selector: 'app-ai-adjustment-section-value',
  templateUrl: './ai-adjustment-section-value.component.html',
  styleUrls: ['./ai-adjustment-section-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AiAdjustmentSectionValueComponent {
  @Input() data: Record<string, unknown> | null = null;

  public get title(): string | null {
    return this.readString('title');
  }

  public get subtitle(): string | null {
    return this.readString('subtitle');
  }

  /**
   * The section's rich text.
   *
   * Bound with plain `[innerHTML]` in the template so Angular's sanitizer runs.
   * This is model output, so the `safe:'html'` pipe would be exactly the wrong
   * tool: it bypasses sanitization by design.
   */
  public get content(): string | null {
    return this.readString('content');
  }

  /**
   * Every list the data carries, in a fixed order so the two comparison columns
   * line up.
   */
  public get lists(): IValueList[] {
    const lists: IValueList[] = [];

    // Material list items carry a name plus optional amount, unit and category;
    // flattened to one readable line each, since the comparison view is about
    // what changed, not about editing the row.
    //
    const items = this.readArray('items');
    if (items.length > 0) {
      lists.push({
        labelKey: null,
        items: items.map((item) => this.describeMaterialItem(item)),
      });
    }

    const material = this.readArray('material');
    if (material.length > 0) {
      lists.push({
        labelKey: null,
        items: material.map((item) => this.describeNamedItem(item)),
      });
    }

    const terms = this.readArray('term');
    if (terms.length > 0) {
      lists.push({
        labelKey: null,
        items: terms.map((term) => this.describeTerm(term)),
      });
    }

    const sources = this.readArray('source');
    if (sources.length > 0) {
      lists.push({ labelKey: null, items: sources.map((source) => `${source}`) });
    }

    const videos = this.readArray('video');
    if (videos.length > 0) {
      lists.push({ labelKey: null, items: videos.map((video) => `${video}`) });
    }

    // Media is reported by count: the comparison view has no room for image
    // previews, and the AI never changes media anyway.
    //
    const media = this.readArray('media');
    if (media.length > 0) {
      lists.push({
        labelKey: 'activity.ai_adjustment.compare.media_count',
        items: media.map((item) => this.readStringFrom(item, 'description') ?? this.readStringFrom(item, 'caption') ?? '—'),
      });
    }

    return lists;
  }

  /** True when there is nothing at all to show, e.g. a page break. */
  public get isEmpty(): boolean {
    return isNil(this.title) && isNil(this.subtitle) && isNil(this.content) && this.lists.length === 0;
  }

  private readString(key: string): string | null {
    return this.readStringFrom(this.data, key);
  }

  private readStringFrom(source: unknown, key: string): string | null {
    const value = (source as Record<string, unknown>)?.[key];

    if (isNil(value)) {
      return null;
    }

    const text = `${value}`.trim();

    return text.length > 0 ? text : null;
  }

  private readArray(key: string): unknown[] {
    const value = this.data?.[key];

    return Array.isArray(value) ? value : [];
  }

  private describeMaterialItem(item: unknown): string {
    const name = this.readStringFrom(item, 'name') ?? '';
    const amount = this.readStringFrom(item, 'amount');
    const unit = this.readStringFrom(item, 'unit');
    const quantity = [amount, unit].filter((part) => !isNil(part)).join(' ');

    return quantity.length > 0 ? `${quantity} ${name}`.trim() : name;
  }

  private describeNamedItem(item: unknown): string {
    const name = this.readStringFrom(item, 'name') ?? '';
    const description = this.readStringFrom(item, 'description');

    return isNil(description) ? name : `${name} — ${description}`;
  }

  private describeTerm(term: unknown): string {
    const name = this.readStringFrom(term, 'name') ?? '';
    const description = this.readStringFrom(term, 'description');

    return isNil(description) ? name : `${name}: ${description}`;
  }
}
